import { T } from 'gt-react';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowSquareOutIcon,
  CircleNotchIcon,
  FileTextIcon,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { PDFViewer } from '@/components/common/pdf-viewer';
import {
  OfficeDocViewer,
  isOfficeViewerSupported,
  useIsOfficeViewerEnabled,
} from '@/components/common/OfficeDocViewer';
import { ExcelDocViewer } from '@/components/xlsx-viewer/ExcelDocViewer';
import { InboundEmailViewer } from '@/components/email-viewer/InboundEmailViewer';
import { useDocumentPreview } from '@/api/rpc/hooks/core/useDocuments';
import { useInboundEmailByDocumentId } from '@/api/rpc/hooks/communications/useInboundEmails';
import { isEmailBodyDocument } from '@erp/shared-utils';
import { cn } from '@/lib/utils';
import { isSpreadsheetDocumentPreview } from './documentSpreadsheetPreview';

interface DocumentViewerProps {
  /** Core document id — the viewer fetches its own signed URL + metadata. */
  documentId: string;
  className?: string;
  previewPlainTextAsTable?: boolean;
}

function getExtension(name: string | null | undefined): string {
  if (!name) return '';
  return name.split('.').pop()?.toLowerCase() ?? '';
}

function isPlainText(extension: string, mimeType: string | null | undefined) {
  return mimeType?.startsWith('text/plain') === true || extension === 'txt';
}

/**
 * A plain-text file shown as-is (a bank memo rendered as a remittance, for
 * one). Keyed on the document, not the signed URL, so a refreshed URL does
 * not refetch the same bytes.
 */
function PlainTextPreview({
  documentId,
  signedUrl,
  className,
}: {
  documentId: string;
  signedUrl: string;
  className?: string;
}) {
  const {
    data: text,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['document-plain-text', documentId],
    queryFn: async () => {
      const response = await fetch(signedUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.text();
    },
    staleTime: Infinity,
  });

  if (isLoading) {
    return (
      <div className={cn('h-full flex items-center justify-center', className)}>
        <CircleNotchIcon className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (error || text == null) {
    return (
      <div
        className={cn(
          'h-full flex items-center justify-center text-muted-foreground text-sm',
          className,
        )}
      >
        <T context="document preview unavailable">No preview available</T>
      </div>
    );
  }
  return (
    <pre
      className={cn(
        'h-full overflow-auto whitespace-pre-wrap break-words p-4 font-mono text-sm',
        className,
      )}
    >
      {text}
    </pre>
  );
}

/**
 * Renders an inline preview for a core document given its id. Dispatches to the
 * appropriate type-specific viewer (PDF, image, Excel, plain text, Office) and falls back to
 * an "open in new tab" affordance for unsupported types.
 */
export function DocumentViewer({
  documentId,
  className,
  previewPlainTextAsTable = false,
}: DocumentViewerProps) {
  const { data, isLoading, error, refetch } = useDocumentPreview(documentId);
  const isOfficeEnabled = useIsOfficeViewerEnabled();

  // Docified email bodies are HTML renders of an inbound email's body — there's
  // nothing useful to preview as a file. Resolve the owning inbound email and
  // show the smart email view instead (gated so non-email docs pay nothing).
  const isEmailBody = isEmailBodyDocument(data?.fileName);
  const { data: emailLink, isLoading: isEmailLinkLoading } =
    useInboundEmailByDocumentId(isEmailBody ? documentId : null);

  if (isLoading) {
    return (
      <div className={cn('h-full flex items-center justify-center', className)}>
        <CircleNotchIcon className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !data?.signedUrl) {
    return (
      <div
        className={cn(
          'h-full flex items-center justify-center text-muted-foreground text-sm',
          className,
        )}
      >
        <T context="document preview unavailable">No preview available</T>
      </div>
    );
  }

  // Resolved a source inbound email for this docified body — render it as an
  // email. When there's no linked email, fall through to the normal file
  // dispatch below so the user still sees something.
  if (isEmailBody) {
    if (isEmailLinkLoading) {
      return (
        <div
          className={cn('h-full flex items-center justify-center', className)}
        >
          <CircleNotchIcon className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      );
    }
    if (emailLink?.inboundEmailId) {
      return (
        <div className={cn('h-full', className)}>
          <InboundEmailViewer inboundEmailId={emailLink.inboundEmailId} />
        </div>
      );
    }
  }

  const { fileName, mimeType, signedUrl } = data;
  const extension = getExtension(fileName);
  const isPdf = mimeType === 'application/pdf' || extension === 'pdf';
  const isImage = mimeType?.startsWith('image/') ?? false;
  const isSpreadsheet = isSpreadsheetDocumentPreview(
    extension,
    mimeType,
    previewPlainTextAsTable,
  );

  if (isPdf) {
    return (
      <div className={cn('h-full', className)}>
        <PDFViewer url={signedUrl} onUrlExpired={() => refetch()} />
      </div>
    );
  }

  if (isImage) {
    return (
      <div
        className={cn(
          'h-full overflow-auto flex items-center justify-center p-4 bg-muted/30',
          className,
        )}
      >
        <img
          src={signedUrl}
          alt={fileName}
          className="max-h-full max-w-full object-contain border rounded"
        />
      </div>
    );
  }

  if (isSpreadsheet) {
    return (
      <div className={cn('h-full overflow-auto', className)}>
        <ExcelDocViewer
          url={signedUrl}
          fileName={fileName}
          mimeType={mimeType ?? undefined}
          documentId={documentId}
          height="100%"
          previewPlainTextAsTable={previewPlainTextAsTable}
        />
      </div>
    );
  }

  if (isPlainText(extension, mimeType)) {
    return (
      <PlainTextPreview
        documentId={documentId}
        signedUrl={signedUrl}
        className={className}
      />
    );
  }

  if (isOfficeEnabled && isOfficeViewerSupported(fileName)) {
    return (
      <div className={cn('h-full', className)}>
        <OfficeDocViewer documentId={documentId} width="100%" height="100%" />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'h-full flex flex-col items-center justify-center gap-3 p-8 text-center',
        className,
      )}
    >
      <FileTextIcon className="h-12 w-12 text-muted-foreground" />
      <p className="text-sm text-muted-foreground">
        <T context="document preview unsupported type">
          Preview not available for this file type.
        </T>
      </p>
      <Button
        variant="outline"
        size="sm"
        onClick={() => window.open(signedUrl, '_blank', 'noopener,noreferrer')}
      >
        <ArrowSquareOutIcon className="h-4 w-4 mr-1" />
        <T context="document action, open in new tab">Open in new tab</T>
      </Button>
    </div>
  );
}
